import ApiService from '@/services/api.service'
import session from "@/store/session"

class AuthenticationError extends Error {
    constructor(errorCode, message) {
        super(message)
        this.name = this.constructor.name
        this.message = message
        this.errorCode = errorCode
    }
}

const buildAuthenticationError = (error, fallbackMessage) => {
    const status = error && error.response ? error.response.status : null
    const data = error && error.response ? error.response.data : null
    const message = (data && (data.detail || data.message || data.error)) || error.message || fallbackMessage
    return new AuthenticationError(status, message)
}

class UserError extends Error {
    constructor(errorCode, message) {
        super(message)
        this.name = this.constructor.name
        this.message = message
        this.errorCode = errorCode
    }
}

const buildUserError = (error, fallbackMessage) => {
    const status = error && error.response ? error.response.status : null
    const data = error && error.response ? error.response.data : null
    const message = (data && (data.detail || data.message || data.error)) || error.message || fallbackMessage
    return new UserError(status, message)
}

const noCacheRequestConfig = () => ({
    params: { _ts: Date.now() }
})

const normalizeUser = (raw) => {
    if (!raw) {
        return { id: null, name: "", email: "" }
    }
    const nameFromFields = `${raw.firstName || ""} ${raw.lastName || ""}`.trim()
    return {
        ...raw,
        id: raw.id || raw._id || raw.userId || null,
        name: raw.name || nameFromFields || raw.email || "Utilisateur",
        email: raw.email || ""
    }
}

const UserService = {
    
    register: async function(userData) {
        try {
            const response = await ApiService.post("/auth/register", userData);
            return response
        } catch (error) {
            throw buildAuthenticationError(error, "Impossible de créer le compte.")
        }
    },

    ouvreurCreateAccount: async function(userData) {
        try {
            const response = await ApiService.post("ouvreurs/users", userData);
            return response
        } catch (error) {
            throw buildAuthenticationError(error, "Impossible de créer le compte.")
        }
    },


    login: async function(email, password) {

        try {
            const response = await ApiService.post("/auth/login", {
                email: (email || "").trim().toLowerCase(),
                password: password
            });

            return response

        } catch (error) {
            throw buildAuthenticationError(error, "Impossible de se connecter.")
        }
    },

    activateAccount: async function(code) {
        try {
            const response = await ApiService.get(`/auth/activate/${encodeURIComponent(code)}`);
            return response;
        } catch (error) {
            throw buildAuthenticationError(error, "Impossible d'activer le compte.");
        }
    },

    requestPasswordReset: async function(email) {
        try {
            const response = await ApiService.post("/auth/password-reset/request", {
                email: (email || "").trim().toLowerCase()
            });
            return response;
        } catch (error) {
            throw buildAuthenticationError(error, "Impossible d'envoyer la demande de reinitialisation.");
        }
    },

    resetPassword: async function(code, password) {
        try {
            const response = await ApiService.post(`/auth/password-reset/${encodeURIComponent(code)}`, { password });
            return response;
        } catch (error) {
            throw buildAuthenticationError(error, "Impossible de reinitialiser le mot de passe.");
        }
    },

    refreshSession: async function() {
        try {
            const res = await ApiService.get("/auth/me", noCacheRequestConfig());

            if (res.data.user) {
                session.setUser(res.data.user);
                return res.data;
            }
            session.clear()
            return { user: null }
        } catch (error) {
            session.clear()
            throw buildAuthenticationError(error, "Impossible de rafraîchir la session.")
        }

    },

    logout: async function() {
        try {
            await ApiService.post("/auth/logout", {}, noCacheRequestConfig());
            session.clear();
        } catch (error) {
            throw buildAuthenticationError(error, "Impossible de se déconnecter.")
        }
    },

    searchUsers: async function({ search = "", limit = 8, signal } = {}) {
        try {
            const query = new URLSearchParams({
                search: String(search).trim(),
                limit: String(limit)
            }).toString()
            const response = await ApiService.get(`/ouvreurs/users/?${query}`, { signal })
            const data = response.data
            const list = Array.isArray(data) ? data : data.users || []
            return list.map(normalizeUser).filter(user => user.id)
        } catch (error) {
            throw buildUserError(error, "Impossible de recuperer les utilisateurs.")
        }
    },

    getCovoitProfiles: async function() {
        try {
            const response = await ApiService.get("/auth/covoit-profiles")
            return response
        } catch (error) {
            throw buildUserError(error, "Impossible de récupérer les profils de covoit.")
        }
    },

    createCovoitProfile: async function(payload) {
        try {
            const response = await ApiService.post("/auth/covoit-profiles", payload)
            return response
        } catch (error) {
            throw buildUserError(error, "Impossible de sauvegarder le profil.")
        }
    },

    deleteCovoitProfile: async function(profileId) {
        try {
            const response = await ApiService.delete(`/auth/covoit-profiles/${profileId}`)
            return response
        } catch (error) {
            throw buildUserError(error, "Impossible de supprimer le profil.")
        }
    },

    updateNewsletter: async function(newsletter) {
        try {
            const response = await ApiService.patches("/auth/me/newsletter", { newsletter })
            return response
        } catch (error) {
            throw buildUserError(error, "Impossible de mettre à jour la préférence newsletter.")
        }
    },

    updateNotifyNewOuvertureDays: async function(notifyNewOuvertureDays) {
        try {
            const response = await ApiService.patches("/auth/me/notify-new-ouverture-days", { notifyNewOuvertureDays })
            return response
        } catch (error) {
            throw buildUserError(error, "Impossible de mettre à jour la préférence de notification d'ouverture.")
        }
    },

    updateNotifyCovoitInscription: async function(notifyCovoitInscription) {
        try {
            const response = await ApiService.patches("/auth/me/notify-covoit-inscription", { notifyCovoitInscription })
            return response
        } catch (error) {
            throw buildUserError(error, "Impossible de mettre à jour la préférence de notification de covoiturage.")
        }
    }
}

export default UserService

export { UserService, AuthenticationError, UserError }
